import { format } from "date-fns";

/**
 * Extracts the current timezone offset
 *
 * @returns The current timezone offset
 *
 * @example
 * extractCurrentTimezoneOffset() // "+07:00"
 */
export function extractCurrentTimezoneOffset(): string {
  const offset = format(new Date(), "xxx");
  return offset;
}

/**
 * Checks if a date string already contains a timezone offset
 *
 * @param dateString - The date string to check
 * @returns true if the date string contains a timezone offset, false otherwise
 *
 * @example
 * hasTimezoneOffset("2023-12-25T10:30:00+07:00") // true
 * hasTimezoneOffset("2023-12-25T10:30:00-05:00") // true
 * hasTimezoneOffset("2023-12-25T10:30:00Z") // false
 * hasTimezoneOffset("2023-12-25T10:30:00") // false
 */
export function hasTimezoneOffset(dateString: string): boolean {
  const timezoneOffsetRegex = /[+-]\d{1,2}:?\d{0,2}$/;
  return timezoneOffsetRegex.test(dateString);
}

/**
 * Replaces timezone offset in a date string with a new offset
 *
 * @param dateString - The date string to process
 * @param offset - The new timezone offset to replace with
 * @returns The date string with the new timezone offset
 *
 * @example
 * replaceTimezoneOffset("2023-12-25T10:30:00+07:00", "+05:00") // "2023-12-25T10:30:00+05:00"
 * replaceTimezoneOffset("2023-12-25T10:30:00-05:00", "+03:00") // "2023-12-25T10:30:00+03:00"
 * replaceTimezoneOffset("2023-12-25T10:30:00Z", "+07:00") // "2023-12-25T10:30:00+07:00"
 * replaceTimezoneOffset("2023-12-25T10:30:00", "+07:00") // "2023-12-25T10:30:00+07:00"
 */
export function replaceTimezoneOffset(
  dateString: string,
  offset: string,
): string {
  const timezoneOffsetRegex = /[+-]\d{1,2}:?\d{0,2}$|Z$/;
  return dateString.replace(timezoneOffsetRegex, offset);
}

/**
 * Formats a Date into a local-time ISO string with timezone offset.
 *
 * Example: 2025-08-11T18:30:00.000+07:00
 *
 * @param date - Date to format
 * @param includeMilliseconds - Whether to include milliseconds in the output (default true)
 * @returns ISO string in local time with offset, or undefined if date is falsy
 */
export function toLocalIso(
  date: Date | string | number | null | undefined,
  includeMilliseconds: boolean = true,
): string | undefined {
  if (!date) return undefined;
  const d = new Date(date);
  const pattern = includeMilliseconds
    ? "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"
    : "yyyy-MM-dd'T'HH:mm:ssxxx";
  return format(d, pattern);
}
